/**
 * QR code, by hand, for the same reason Code 128 is by hand
 *
 * The shell has one runtime dependency (`electron-updater`) and what goes on paper is built here, so
 * a page from the network cannot hand the printer a picture. A symbology is a fixed specification
 * and does not change; a package for it is an update path into the thing that prints money.
 *
 * WHAT IS IMPLEMENTED, AND WHAT DELIBERATELY IS NOT
 *
 * Byte mode, error correction level **M**, versions **1 to 9** - up to 180 characters, which is four
 * times what a ticket code and a control code need (`ABCDEFGHIJ-KLMN` is fifteen). Anything longer
 * throws rather than silently picking a bigger symbol: this prints codes, and a code that long is a
 * caller passing the wrong thing.
 *
 * Level M over L because paper from a thermal printer smudges and gets folded into a pocket, and
 * over Q/H because those spend modules on redundancy this does not need at these lengths.
 *
 * Kanji and alphanumeric modes are not here. Alphanumeric would be denser for codes, which are
 * upper-case and digits - but it is a second encoder to get right, and at this length the symbol is
 * the same physical size either way.
 *
 * HOW IT IS CHECKED
 *
 * A QR encoder that is subtly wrong produces something that **looks** like a QR code and does not
 * scan, which is the worst failure this file could have. `tests/device/device.test.js` therefore
 * checks the parts that are specified exactly and independently verifiable: the format information
 * bit string against the published table, Reed-Solomon against a known remainder, the module count
 * per version, and the finder and timing patterns by position.
 *
 * That is not the same as a scanner reading it, and the first symbol that goes to a shop should be
 * held under one.
 */

/**
 * The symbol while it is being built.
 *
 * `null` is a module nobody has written yet, which is why the grid starts filled with it rather than
 * with zero: a light module and an unset one are two different statements while the pattern is going
 * down, and `fixed` is what says which of them may still be masked. By the time `matrix` answers,
 * every cell has been written.
 */
export type Grid = Array<Array<number | null>>;

/** which modules the mask must leave alone - the finders, the timing, the format area */
type Reserved = boolean[][];

/* the generator polynomial's log/antilog tables over GF(256), x^8 + x^4 + x^3 + x^2 + 1 */
const EXP: number[] = new Array(512).fill(0);
const LOG: number[] = new Array(256).fill(0);

(() =>
{
    let x = 1;

    for (let i = 0; i < 255; i += 1)
    {
        EXP[i] = x;
        LOG[x] = i;
        x <<= 1;

        if (x & 0x100)
        {
            x ^= 0x11d;
        }
    }

    for (let i = 255; i < 512; i += 1)
    {
        EXP[i] = EXP[i - 255];
    }
})();

const mul = (a: number, b: number): number => (
    (a === 0 || b === 0) ? 0 : EXP[LOG[a] + LOG[b]]
);

/**
 * Reed-Solomon remainder: the error correction codewords for one block.
 *
 * @param data the block's data codewords
 * @param count how many correction codewords the version and level ask for
 */
export const remainder = (data: number[], count: number): number[] =>
{
    // generator polynomial for `count` symbols, built by multiplying out (x - a^i)
    let generator = [1];

    for (let i = 0; i < count; i += 1)
    {
        const next: number[] = new Array(generator.length + 1).fill(0);

        for (let j = 0; j < generator.length; j += 1)
        {
            next[j] ^= generator[j];
            next[j + 1] ^= mul(generator[j], EXP[i]);
        }

        generator = next;
    }

    const out: number[] = new Array(count).fill(0);

    for (const byte of data)
    {
        const factor = byte ^ out[0];

        out.shift();
        out.push(0);

        if (factor !== 0)
        {
            for (let i = 0; i < count; i += 1)
            {
                out[i] ^= mul(generator[i + 1], factor);
            }
        }
    }

    return out;
};

/**
 * Per version, at level M: total data codewords, EC codewords per block, and how many blocks.
 *
 * Straight out of the specification's table.
 *
 * Nine, and that number is a boundary rather than a limit somebody felt like: the character count in
 * byte mode is **eight bits up to version 9** and sixteen from ten onwards, so version 10 is not one
 * more row in this table but a second shape of the header. Nine holds 180 characters - more than any
 * `otpauth://` URI or ticket address this system produces. The day something needs more, what has to
 * change is `codewordsOf`, and this is the warning.
 */
export const VERSIONS: Record<number, { data: number; ecPerBlock: number; blocks: number[] }> = {
    1: { data: 16, ecPerBlock: 10, blocks: [16] },
    2: { data: 28, ecPerBlock: 16, blocks: [28] },
    3: { data: 44, ecPerBlock: 26, blocks: [44] },
    4: { data: 64, ecPerBlock: 18, blocks: [32, 32] },
    5: { data: 86, ecPerBlock: 24, blocks: [43, 43] },
    6: { data: 108, ecPerBlock: 16, blocks: [27, 27, 27, 27] },
    7: { data: 124, ecPerBlock: 18, blocks: [31, 31, 31, 31] },
    8: { data: 154, ecPerBlock: 22, blocks: [38, 38, 39, 39] },
    9: { data: 182, ecPerBlock: 22, blocks: [36, 36, 36, 37, 37] },
};

/** where the alignment pattern sits, per version; version 1 has none */
const ALIGNMENT: Record<number, number[]> = {
    1: [],
    2: [6, 18],
    3: [6, 22],
    4: [6, 26],
    5: [6, 30],
    6: [6, 34],
    7: [6, 22, 38],
    8: [6, 24, 42],
    9: [6, 26, 46],
};

export const sizeOf = (version: number): number => (version * 4) + 17;

/**
 * The smallest version that holds this many bytes.
 *
 * Four bits of mode, eight of length, then the data - so the payload has to fit in
 * `data * 8 - 12` bits.
 */
export const versionFor = (length: number): number =>
{
    for (const version of [1, 2, 3, 4, 5, 6, 7, 8, 9])
    {
        if ((length + 2) <= VERSIONS[version].data)
        {
            return version;
        }
    }

    throw new Error(`qrcode: ${length} bajtova je previse za verziju 9`);
};

/** the data codewords: mode, length, payload, terminator, padding */
const codewordsOf = (bytes: number[], version: number): number[] =>
{
    const total = VERSIONS[version].data;
    const bits: number[] = [];
    const push = (value: number, count: number) =>
    {
        for (let i = count - 1; i >= 0; i -= 1)
        {
            bits.push((value >> i) & 1);
        }
    };

    // 0100 is byte mode; the length is eight bits for versions 1-9
    push(4, 4);
    push(bytes.length, 8);

    for (const byte of bytes)
    {
        push(byte, 8);
    }

    // terminator, then up to a byte boundary
    for (let i = 0; i < 4 && bits.length < total * 8; i += 1)
    {
        bits.push(0);
    }

    while (bits.length % 8 !== 0)
    {
        bits.push(0);
    }

    const out: number[] = [];

    for (let i = 0; i < bits.length; i += 8)
    {
        out.push(bits.slice(i, i + 8).reduce((value, bit) => (value << 1) | bit, 0));
    }

    // the specified filler, alternating, until the symbol is full
    const filler = [0xec, 0x11];

    while (out.length < total)
    {
        out.push(filler[(out.length - (bits.length / 8)) % 2]);
    }

    return out;
};

/**
 * Data and error correction codewords, interleaved as the specification orders them.
 *
 * With one block that is simply data followed by EC; with two it matters, and version 4 has two.
 */
const finalCodewords = (data: number[], version: number): number[] =>
{
    const { blocks, ecPerBlock } = VERSIONS[version];
    const dataBlocks: number[][] = [];
    const ecBlocks: number[][] = [];
    let at = 0;

    for (const size of blocks)
    {
        const block = data.slice(at, at + size);

        at += size;
        dataBlocks.push(block);
        ecBlocks.push(remainder(block, ecPerBlock));
    }

    const out: number[] = [];
    const longest = Math.max(...blocks);

    for (let i = 0; i < longest; i += 1)
    {
        for (const block of dataBlocks)
        {
            if (i < block.length)
            {
                out.push(block[i]);
            }
        }
    }

    for (let i = 0; i < ecPerBlock; i += 1)
    {
        for (const block of ecBlocks)
        {
            out.push(block[i]);
        }
    }

    return out;
};

/** the fixed patterns: finders, separators, timing, and the one dark module */
const skeleton = (version: number): { grid: Grid; fixed: Reserved } =>
{
    const size = sizeOf(version);
    const grid: Grid = Array.from({ length: size }, () => new Array(size).fill(null));
    const fixed: Reserved = Array.from({ length: size }, () => new Array(size).fill(false));
    const put = (row: number, column: number, value: number) =>
    {
        grid[row][column] = value;
        fixed[row][column] = true;
    };
    const finder = (top: number, left: number) =>
    {
        for (let r = -1; r <= 7; r += 1)
        {
            for (let c = -1; c <= 7; c += 1)
            {
                const row = top + r;
                const column = left + c;

                if (row < 0 || row >= size || column < 0 || column >= size)
                {
                    continue;
                }

                const ring = Math.max(Math.abs(r - 3), Math.abs(c - 3));

                put(row, column, (r >= 0 && r <= 6 && c >= 0 && c <= 6 && ring !== 2) ? 1 : 0);
            }
        }
    };

    finder(0, 0);
    finder(0, size - 7);
    finder(size - 7, 0);

    for (let i = 8; i < size - 8; i += 1)
    {
        put(6, i, (i % 2 === 0) ? 1 : 0);
        put(i, 6, (i % 2 === 0) ? 1 : 0);
    }

    /**
     * An alignment pattern is skipped where a **finder** is, and nowhere else.
     *
     * The test used to be "is this module already fixed", which is the same answer up to version 6 and
     * the wrong one from seven on: the centres there include 22, 24 and 26 on row and column six, so a
     * pattern sits **on the timing line** and has to be drawn over it. Reading "already fixed" as
     * "leave it alone" silently dropped two of the six patterns of a version 7 symbol.
     */
    const inFinder = (row: number, column: number) => (
        (row <= 8 && column <= 8)
        || (row <= 8 && column >= size - 9)
        || (row >= size - 9 && column <= 8)
    );

    for (const row of ALIGNMENT[version])
    {
        for (const column of ALIGNMENT[version])
        {
            if (inFinder(row, column))
            {
                continue;
            }

            for (let r = -2; r <= 2; r += 1)
            {
                for (let c = -2; c <= 2; c += 1)
                {
                    put(row + r, column + c, (Math.max(Math.abs(r), Math.abs(c)) !== 1) ? 1 : 0);
                }
            }
        }
    }

    /**
     * From version 7 the symbol says which version it is, twice
     *
     * Eighteen bits - six of version and twelve of BCH(18,6) - in a 3x6 block beside the bottom-left
     * finder and its transpose beside the top-right one. Up to version 6 a decoder works the size out
     * of the picture; from seven it reads this, and a symbol without it is one nothing scans.
     */
    if (version >= 7)
    {
        /**
         * BCH(18,6): the version shifted through the generator twelve times.
         *
         * The first attempt wrote this as the long division the format bits use and aligned the
         * generator against the wrong bit - which produced a number far wider than eighteen bits, a
         * symbol that looked right and that no decoder read. The published values are the check:
         * version 7 is `0x07C94`, 8 is `0x085BC`, 9 is `0x09A99`.
         */
        let rem = version;

        for (let i = 0; i < 12; i += 1)
        {
            rem = ((rem << 1) ^ (((rem >> 11) & 1) * 0x1F25)) & 0x1FFF;
        }

        const info = ((version << 12) | (rem & 0xFFF)) >>> 0;

        for (let i = 0; i < 18; i += 1)
        {
            const bit = (info >> i) & 1;
            const near = Math.floor(i / 3);
            const far = size - 11 + (i % 3);

            put(far, near, bit);
            put(near, far, bit);
        }
    }

    // the module that is always dark, and the format areas, reserved so data skips them
    put(size - 8, 8, 1);

    for (let i = 0; i < 9; i += 1)
    {
        if (!fixed[8][i])
        {
            put(8, i, 0);
        }

        if (!fixed[i][8])
        {
            put(i, 8, 0);
        }
    }

    for (let i = 0; i < 8; i += 1)
    {
        if (!fixed[8][size - 1 - i])
        {
            put(8, size - 1 - i, 0);
        }

        if (!fixed[size - 1 - i][8])
        {
            put(size - 1 - i, 8, 0);
        }
    }

    return { grid, fixed };
};

/** the eight masks, by number */
const MASKS: Array<(r: number, c: number) => boolean> = [
    (r, c) => ((r + c) % 2 === 0),
    (r) => (r % 2 === 0),
    (_r, c) => (c % 3 === 0),
    (r, c) => ((r + c) % 3 === 0),
    (r, c) => ((Math.floor(r / 2) + Math.floor(c / 3)) % 2 === 0),
    (r, c) => (((r * c) % 2) + ((r * c) % 3) === 0),
    (r, c) => (((((r * c) % 2) + ((r * c) % 3)) % 2) === 0),
    (r, c) => (((((r + c) % 2) + ((r * c) % 3)) % 2) === 0),
];

/**
 * Format information: error correction level and mask, BCH coded and masked.
 *
 * **Level M is `0b00`, not `0b10`.** The five level values are not in the obvious order - L is 01,
 * M is 00, Q is 11, H is 10 - and using the wrong pair produces a symbol that is structurally
 * perfect and reads as another error level, so every codeword is then misread. That was the first
 * thing the test caught.
 *
 * The generator is 0x537 and the result is xored with 0x5412, both from the specification;
 * `tests/device/device.test.js` checks one value against the published table.
 */
export const formatBits = (mask: number): number[] =>
{
    const data = (0b00 << 3) | mask;
    let value = data << 10;

    // BCH(15,5): divide by the generator until what is left fits in ten bits
    for (let i = 4; i >= 0; i -= 1)
    {
        if (value & (1 << (i + 10)))
        {
            value ^= 0x537 << i;
        }
    }

    value = ((data << 10) | value) ^ 0x5412;

    const out: number[] = [];

    for (let i = 14; i >= 0; i -= 1)
    {
        out.push((value >> i) & 1);
    }

    return out;
};

/** how badly a finished symbol reads, by the four penalty rules */
const penalty = (grid: Grid): number =>
{
    const size = grid.length;
    let score = 0;
    const run = (values: Array<number | null>) =>
    {
        let out = 0;
        let length = 1;

        for (let i = 1; i < values.length; i += 1)
        {
            if (values[i] === values[i - 1])
            {
                length += 1;
                continue;
            }

            if (length >= 5)
            {
                out += 3 + (length - 5);
            }

            length = 1;
        }

        return out + ((length >= 5) ? 3 + (length - 5) : 0);
    };

    for (let i = 0; i < size; i += 1)
    {
        score += run(grid[i]);
        score += run(grid.map((row) => row[i]));
    }

    for (let r = 0; r < size - 1; r += 1)
    {
        for (let c = 0; c < size - 1; c += 1)
        {
            const one = grid[r][c];

            if (one === grid[r][c + 1] && one === grid[r + 1][c] && one === grid[r + 1][c + 1])
            {
                score += 3;
            }
        }
    }

    const finderish = [1, 0, 1, 1, 1, 0, 1, 0, 0, 0, 0];
    const reversed = [...finderish].reverse();
    const scan = (values: Array<number | null>) =>
    {
        for (let i = 0; i + 11 <= values.length; i += 1)
        {
            const window = values.slice(i, i + 11);

            if (finderish.every((bit, at) => bit === window[at]) || reversed.every((bit, at) => bit === window[at]))
            {
                score += 40;
            }
        }
    };

    for (let i = 0; i < size; i += 1)
    {
        scan(grid[i]);
        scan(grid.map((row) => row[i]));
    }

    const dark = grid.flat().filter((one) => one === 1).length;
    const percent = (dark * 100) / (size * size);

    return score + (Math.floor(Math.abs(percent - 50) / 5) * 10);
};

/**
 * The finished module matrix: 1 is dark, 0 is light.
 *
 * @param text what goes in the symbol
 * @returns square matrix, without the quiet zone - the SVG adds that
 */
export const matrix = (text: string): Grid =>
{
    /**
     * `TextEncoder`, not `Buffer`.
     *
     * This file runs **in the browser** too (`SlipPrint.vue` draws the symbol), and there `Buffer`
     * does not exist: the slip page threw `ReferenceError: Buffer is not defined` out of the middle
     * of the computed property and the paper came out **blank**. Nothing said so on the paper - a
     * slip that is not printed is a payin the player has nothing to show for.
     *
     * `TextEncoder` is a global in both, and it encodes UTF-8 by definition - which is exactly what was
     * being asked of `Buffer.from(text, 'utf8')`.
     */
    const bytes = Array.from(new TextEncoder().encode(String(text)));
    const version = versionFor(bytes.length);
    const size = sizeOf(version);
    const codewords = finalCodewords(codewordsOf(bytes, version), version);
    const { grid, fixed } = skeleton(version);

    // data goes up and down in two-module columns, right to left, skipping the timing column
    let bit = 0;
    let upward = true;
    const bitAt = (index: number) =>
    {
        const byte = codewords[Math.floor(index / 8)];

        return (byte === undefined) ? 0 : ((byte >> (7 - (index % 8))) & 1);
    };

    for (let right = size - 1; right >= 1; right -= 2)
    {
        const column = (right === 6) ? right - 1 : right;

        for (let step = 0; step < size; step += 1)
        {
            const row = upward ? (size - 1 - step) : step;

            for (const at of [column, column - 1])
            {
                if (fixed[row][at])
                {
                    continue;
                }

                grid[row][at] = bitAt(bit);
                bit += 1;
            }
        }

        upward = !upward;
    }

    /*
     * Which mask won is not needed: the format bits of the winning mask are already written into `best`
     * inside the loop, so the number itself has no reader.
     */
    let best: Grid = [];
    let bestScore = Infinity;

    for (let mask = 0; mask < 8; mask += 1)
    {
        const candidate: Grid = grid.map((row, r) => row.map((value, c) => (fixed[r][c] ? value : ((value as number) ^ (MASKS[mask](r, c) ? 1 : 0)))));
        const bits = formatBits(mask);

        for (let i = 0; i < 15; i += 1)
        {
            const [row, column] = (i < 6) ? [8, i]
                : (i === 6) ? [8, 7]
                    : (i === 7) ? [8, 8]
                        : (i === 8) ? [7, 8]
                            : [14 - i, 8];

            candidate[row][column] = bits[i];

            /**
             * The second copy is **seven** modules down the left and eight along the top.
             *
             * Eight and seven is the obvious split and it is wrong: the module at `(size - 8, 8)` is
             * the one that is always dark, not a format bit. Taking it overwrote that module with a
             * zero - a symbol that looks right and fails validation, and the second thing the test
             * caught.
             */
            const [mirrorRow, mirrorColumn] = (i < 7) ? [size - 1 - i, 8] : [8, size - 15 + i];

            candidate[mirrorRow][mirrorColumn] = bits[i];
        }

        const score = penalty(candidate);

        if (score < bestScore)
        {
            bestScore = score;
            best = candidate;
        }
    }

    return best;
};

/**
 * The symbol as SVG, ready to sit in the printed page.
 *
 * One `<rect>` per dark module rather than a path: the print window has no scripts and no network,
 * and rectangles are the thing every renderer agrees on.
 */
export const svg = (text: string, { module = 3, quiet = 4 }: { module?: number; quiet?: number } = {}): string =>
{
    const grid = matrix(text);
    const size = grid.length;
    const total = (size + (quiet * 2)) * module;
    const parts: string[] = [];

    for (let row = 0; row < size; row += 1)
    {
        for (let column = 0; column < size; column += 1)
        {
            if (grid[row][column] === 1)
            {
                parts.push(`<rect x="${(column + quiet) * module}" y="${(row + quiet) * module}" `
                    + `width="${module}" height="${module}"/>`);
            }
        }
    }

    return `<svg xmlns="http://www.w3.org/2000/svg" width="${total}" height="${total}" `
        + `viewBox="0 0 ${total} ${total}"><rect width="${total}" height="${total}" fill="#fff"/>`
        + `<g fill="#000">${parts.join('')}</g></svg>`;
};

export default {
    matrix, svg, formatBits, remainder, versionFor, sizeOf, VERSIONS,
};
